import { Injectable } from '@angular/core';
import { Observable, of, from } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { StockItem, StockListResponse } from '../stock-search.config';
import {
  GoodsStockControllerService,
  InventoryStatusControllerService,
  StockQueryReqDTO,
  PageInfoGoodsStockResVO,
  GoodsStockResVO,
  InventoryStatusSummaryVO,
  CategoryControllerService,
} from '@app/api';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';

/** API response shape for stock (GoodsStockResVO may omit some fields in generated type) */
type StockApiItem = GoodsStockResVO & {
  lockNum?: number;
  preemptionNum?: number;
  reservedNum?: number;
  wayNum?: number;
};

@Injectable()
export class StockSearchService {
  constructor(
    private goodsStockService: GoodsStockControllerService,
    private categoryService: CategoryControllerService,
    private inventoryStatusService: InventoryStatusControllerService,
  ) {}

  private static pairKey(skuCode?: string, warehouseId?: number): string {
    return `${skuCode}::${warehouseId}`;
  }

  /**
   * Batches IMS's precomputed 5 BRD statuses (available, held, wayNum, defective, onOrder) for
   * every (skuCode, warehouseId) pair in `items` (RBA-2315: consumers must not self-calculate
   * these). A pair with no IMS stock record, or with no batch call made at all (empty input, or
   * the batch call failing), maps to no entry (callers default each field to 0).
   */
  private buildSummaryByPair(
    items: GoodsStockResVO[]
  ): Observable<Map<string, InventoryStatusSummaryVO>> {
    const queryPairs = new Map<string, { skuCode: string; warehouseId: number }>();
    for (const item of items) {
      if (item.skuCode && item.warehouseId != null) {
        queryPairs.set(StockSearchService.pairKey(item.skuCode, item.warehouseId), {
          skuCode: item.skuCode,
          warehouseId: item.warehouseId,
        });
      }
    }

    if (queryPairs.size === 0) {
      return of(new Map<string, InventoryStatusSummaryVO>());
    }

    return this.inventoryStatusService
      .getInventoryStatusSummaryBatch(Array.from(queryPairs.values()))
      .pipe(
        // The generated client requests responseType 'blob' whenever the resolved Accept
        // header isn't recognized as a JSON mime (here it's '*/*') -- same quirk pageStock()
        // already works around above, via BlobConverterUtil.
        switchMap((response: any) => {
          if (BlobConverterUtil.isBlob(response)) {
            return from(BlobConverterUtil.convertToJson(response));
          }
          return of(response);
        }),
        map((summaries: InventoryStatusSummaryVO[]) => {
          const summaryByPair = new Map<string, InventoryStatusSummaryVO>();
          for (const summary of summaries ?? []) {
            summaryByPair.set(
              StockSearchService.pairKey(summary.skuCode, summary.warehouseId),
              summary
            );
          }
          return summaryByPair;
        }),
        catchError(() => of(new Map<string, InventoryStatusSummaryVO>()))
      );
  }

  private mapResponseToStockItem(
    apiItem: GoodsStockResVO,
    summaryByPair: Map<string, InventoryStatusSummaryVO>
  ): StockItem {
    // Create unique ID by combining SKU code and warehouse code
    // This ensures items with same SKU but different warehouses have different IDs
    const item = apiItem as StockApiItem;
    const uniqueId = `${apiItem.skuCode || ''}_${apiItem.warehouseCode || ''}`;
    const stockNum = apiItem.stockNum ?? 0;
    const reservedNum = item.reservedNum ?? 0;
    const preemptionNum = item.preemptionNum ?? 0;
    const lockNum = item.lockNum ?? 0;
    // The 5 BRD statuses are computed by IMS (Stock Service), not re-derived here from raw
    // fields.
    const summary = summaryByPair.get(
      StockSearchService.pairKey(apiItem.skuCode, apiItem.warehouseId)
    );
    const usableNum = summary?.available ?? 0;
    const heldNum = summary?.held ?? 0;
    const wayNum = summary?.wayNum ?? 0;
    const defectiveNum = summary?.defective ?? 0;
    const onOrderNum = summary?.onOrder ?? 0;

    return {
      id: uniqueId,
      brandId: apiItem.brandId?.toString() || '',
      brandName: apiItem.brandName || '',
      categoryName: apiItem.categoryName || '',
      lockNum,
      preemptionNum,
      reservedNum,
      skuCode: apiItem.skuCode || '',
      skuName: apiItem.skuName || '',
      spuCode: apiItem.spuCode || '',
      spuName: apiItem.spuName || '',
      spuType: apiItem.spuType || 0,
      stockNum,
      typeCode: apiItem.typeCode || '',
      usableNum,
      heldNum,
      defectiveNum,
      onOrderNum,
      waitNum: 0, // missing fields
      warehouseCode: apiItem.warehouseCode || '',
      warehouseId: apiItem.warehouseId?.toString() || '',
      warehouseName: apiItem.warehouseName || '',
      wayNum,
      exceedZero: stockNum > 0,
    };
  }

  // Convert search filters to API format
  private mapFiltersFormat(filters?: any): StockQueryReqDTO {
    const apiFilters: StockQueryReqDTO = {
      page: 0, // API uses 0-based pagination
      size: 20,
    };

    if (filters) {
      if (filters.spuCode) {
        apiFilters.spuCode = filters.spuCode;
      }

      if (filters.spuName) {
        apiFilters.spuName = filters.spuName;
      }

      if (filters.warehouseCode) {
        apiFilters.warehouseCode = filters.warehouseCode;
      }

      // if (filters.warehouseName && filters.warehouseId.length > 0) {
      //   // Convert string array to number array
      //   apiFilters.warehouseId = filters.warehouseId;
      // }

      if (filters.skuCode) {
        apiFilters.skuCode = filters.skuCode;
      }

      if (filters.categoryIdList && filters.categoryIdList.length > 0) {
        // Convert string array to number array
        apiFilters.categoryIdList = filters.categoryIdList.map((id: string) =>
          parseInt(id, 10),
        );
      }

      if (filters.exceedZero !== null && filters.exceedZero !== undefined) {
        apiFilters.exceedZero = filters.exceedZero;
      }
    }

    return apiFilters;
  }

  // Get all stock items with pagination and search
  getStockItems(
    page: number = 1,
    pageSize: number = 20,
    filters?: any,
  ): Observable<StockListResponse> {
    // Convert to API format
    const apiFilters = this.mapFiltersFormat(filters);
    apiFilters.page = page - 1; // Convert to 0-based pagination
    apiFilters.size = pageSize;

    return this.goodsStockService.pageStock(apiFilters).pipe(
      switchMap((response: any) => {
        // Handle blob response using BlobConverterUtil
        if (BlobConverterUtil.isBlob(response)) {
          return from(BlobConverterUtil.convertToJson(response));
        }
        return of(response);
      }),
      switchMap((response: PageInfoGoodsStockResVO) => {
        const pageContent = response.pageContent || [];
        return this.buildSummaryByPair(pageContent).pipe(
          map((summaryByPair) => {
            const stockItems = pageContent.map((item) =>
              this.mapResponseToStockItem(item, summaryByPair),
            );
            return {
              data: stockItems,
              totalRecords: response.totalElements || 0,
              loading: false,
            };
          }),
        );
      }),
      catchError((error) => {
        console.error('Error fetching stock data:', error);
        return of({
          data: [],
          totalRecords: 0,
          loading: false,
        });
      }),
    );
  }

  // Get category tree options for search form
  getCategoryTreeOptions(params?: any): Observable<any[]> {
    const page = params?.page ? params?.page - 1 : undefined;
    return this.categoryService
      .getList2(
        undefined,
        undefined,
        undefined,
        undefined,
        params?.statusFlag,
        undefined,
        undefined,
        undefined,
        page,
        params?.size,
      )
      .pipe(
        switchMap((response: any) => {
          // Handle blob response using BlobConverterUtil
          if (BlobConverterUtil.isBlob(response)) {
            return from(BlobConverterUtil.convertToJson(response));
          }
          return of(response);
        }),
        map((response: any) => {
          return this.mapCategoryListToOptions(response);
        }),
        catchError((error) => {
          console.error('Error fetching category options:', error);
          // Return empty array on error
          return of([]);
        }),
      );
  }

  private mapCategoryListToOptions(response: any): any[] {
    if (!response) {
      return [];
    }

    const content = response.content || response.pageContent || response.data;

    if (!content || !Array.isArray(content)) {
      return [];
    }

    return content.map((item: any) => ({
      label: item.name || '',
      value: item.id?.toString() || '',
      // Note: The API returns a flat list, so we need to build the tree structure
      // For now, we'll return a flat structure since the API doesn't include children
    }));
  }
}
